import { TabletButton } from '@campfire/tablet-button';
import { Box, Typography } from '@material-ui/core';
import { Check, AccessTime } from '@material-ui/icons';
import React from 'react';
import { useHistory } from 'react-router';
import { useCampfireTheme } from '../../theme/useCampfireTheme';

const GetStartedButton = () => {
  const { theme } = useCampfireTheme();
  const history = useHistory();

  return (
    <TabletButton
      variant='contained'
      style={{
        backgroundColor: theme.color.secondary.main900,
        color: '#ffffff',
        boxSizing: 'border-box',
        borderRadius: '4px',
        textTransform: 'none',
        padding: '10px 16px',
        fontWeight: 600,
        fontSize: '14px',
      }}
      onClick={() => history.push('/create-trial')}
    >
      Get Started
    </TabletButton>
  );
};

const FeatureItem = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => {
  const { theme, isMobile } = useCampfireTheme();

  return (
    <Box display='flex' alignItems='flex-start' p={isMobile ? 2 : 3}>
      <Box mr={2} mt={0.5}>
        {icon}
      </Box>
      <Box>
        <Typography
          variant='subtitle1'
          style={{
            fontWeight: 600,
            fontSize: isMobile ? '14px' : '16px',
            color: '#343434',
            marginBottom: '4px',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant='body2'
          style={{
            fontSize: isMobile ? '12px' : '14px',
            color: theme.color.grey.neutral400,
            lineHeight: 1.4,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

export const TrialWelcomeScreen = () => {
  const { theme, isSm, isMobile } = useCampfireTheme();

  const features = [
    {
      icon: <Check style={{ color: theme.status.green.medium, fontSize: '20px' }} />,
      title: 'Fully Featured',
      description:
        'Access all Volaby features in your trial - recruitment, rostering, communication, reporting and more.',
    },
    {
      icon: <AccessTime style={{ color: theme.color.secondary.main, fontSize: '20px' }} />,
      title: '30 Days Free',
      description:
        'Enjoy 30-days free, with the ability to onboarding and add users and volunteers. Upgrade at any time.',
    },
  ];

  return (
    <Box mx={isMobile ? '24px' : '34px'} my='28px'>
      {/* Main Heading */}
      <Box mb={isMobile ? 3 : 4}>
        <Typography
          variant='body1'
          style={{
            fontSize: isMobile ? '12px' : '14px',
            color: theme.color.grey.neutral400,
            marginBottom: '2px',
            letterSpacing: 'normal',
          }}
        >
          30 Day Free Trial
        </Typography>
        <Typography
          variant='h2'
          style={{
            fontWeight: 700,
            fontSize: isMobile ? '22px' : '28px',
            marginBottom: isMobile ? '12px' : '14px',
            letterSpacing: '-1px',
            color: '#343434',
          }}
        >
          Welcome
        </Typography>
        <Typography
          variant='body1'
          style={{
            fontSize: '14px',
            color: '#343434',
            lineHeight: 1.6,
            marginBottom: '18px',
          }}
        >
          We think the best way to make a decision on the software that powers your organisation is to have access to
          the product itself. <br />
          Join organisations from across Australia and New Zealand by getting started with our full-featured 30 day
          free-trial, without the lengthy, time-consuming or expensive configuration period. You can recruit, onboard,
          record impact and communicate right out of the box.
        </Typography>
      </Box>

      {/* Features */}
      <Box
        display='grid'
        gridTemplateColumns={isSm ? '1fr' : '1fr 1fr'}
        gap={isMobile ? '10px' : '14px'}
        mb={isMobile ? 3 : 4}
      >
        {features.map((feature) => (
          <FeatureItem
            key={feature.title}
            icon={feature.icon}
            title={feature.title}
            description={feature.description}
          />
        ))}
      </Box>

      {/* Get Started */}
      <Box textAlign='center' mb={isMobile ? 3 : 4}>
        <GetStartedButton />
        <Typography
          variant='body2'
          style={{
            fontSize: isMobile ? '13px' : '14px',
            color: theme.color.grey.neutral400,
            lineHeight: 1.5,
            marginTop: '16px',
          }}
        >
          There&apos;s a wealth of guides and resources to help you along the way, and you can chat to us anytime by
          clicking the chat icon in the bottom right, anywhere in Volaby!
        </Typography>
      </Box>
    </Box>
  );
};
